import { createTheme, type Theme } from '@mui/material/styles';

import { buildComponents } from './components';
import { buildPalette } from './palette';
import { typography } from './typography';
import { radius, shadows, SPACING_UNIT, type ColorMode } from './tokens';

export * from './tokens';
export { MIN_BODY_FONT_PX } from './typography';

/**
 * Builds the application theme for a colour mode.
 *
 * MUI's default `shadows` array is 25 steps of increasingly heavy Material
 * elevation. It is replaced wholesale here with the soft ramp from tokens.ts —
 * leaving it in place means any component that reaches for `elevation={4}`
 * silently reintroduces the look the brief rules out.
 */
export function createAppTheme(mode: ColorMode): Theme {
  const ramp = shadows[mode];
  const shadowStack = [
    ramp.none,
    ramp.xs,
    ramp.sm,
    ramp.sm,
    ramp.md,
    ...Array.from({ length: 20 }, () => ramp.lg),
  ] as Theme['shadows'];

  return createTheme({
    palette: buildPalette(mode),
    typography,
    spacing: SPACING_UNIT,
    shape: { borderRadius: radius.card },
    shadows: shadowStack,
    components: buildComponents(mode),
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    },
  });
}
